import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';

import { BookingTypesStore } from '@fry/booking/lib';
import { BookingItemsStore } from '@fry/booking/lib/booking-items';
import { BlueprintStore } from '@fry/lib/blueprints';
import { EventTypesStore } from '@fry/lib/event-types';
import { RelationsStore } from '@fry/lib/relations';
import { RolesStore } from '@fry/lib/roles';
import { UserFieldsStore } from '@fry/lib/user-fields';
import { iCaseCompare } from '@fry/lib/utils';


export interface CategoryName {
  _id: string;
  name: string;
}


export interface CategoryNameWithCategories extends CategoryName {
  categories: CategoryNameWithCategories[];
}


function categoriesToFlatCategoryNamesReduce(
  categoryNames: { id: string, name: string }[],
  categoryNameWithCategories: CategoryNameWithCategories
): { id: string, name: string }[] {
  return [
    ...categoryNames,
    { id: categoryNameWithCategories._id, name: categoryNameWithCategories.name },
    ...(categoryNameWithCategories.categories ?? []).reduce(
      (accumulator, category) =>
        categoriesToFlatCategoryNamesReduce(accumulator, category),
      [] as { id: string, name: string }[]
    )
  ];
}


function categoriesToFlatCategoryNames(categoryNamesWithCategories: CategoryNameWithCategories[]) {
  return categoryNamesWithCategories.reduce(
    (categoryNames, categoryNameWithCategories) =>
      [
        ...categoryNames,
        ...categoriesToFlatCategoryNamesReduce([] as { id: string, name: string }[], categoryNameWithCategories)
      ],
    [] as { id: string, name: string }[]
  );
}


@Injectable()
export class EditorDataService {
  private bookingOptionNames$: { [key: string]: Observable<{ id: string, name: string }[]> } = {};

  bookingItemNames$: Observable<{ id: string, name: string }[]>;
  bookingTypeNames$: Observable<{ id: string, name: string }[]>;
  bookingStateNames$: Observable<{ id: string, name: string }[]>;
  bookingMetadataNames$: Observable<{ id: string, name: string }[]>;
  roleNames$: Observable<{ id: string, name: string }[]>;
  relationNamesWithCategoryNames$: Observable<{ id: string, name: string, categoryNames: { id: string, name: string }[] }[]>;
  eventTypeGroupNames$: Observable<{ id: string, name: string }[]>;
  blueprintNamesWithCategoryNames$: Observable<{ id: string, name: string, categoryNames: { id: string, name: string }[] }[]>;
  userFields$: Observable<any[]>;
  stringUserFieldNames$: Observable<{ id: string, name: string }[]>;
  dateUserFieldNames$: Observable<{ id: string, name: string }[]>;
  discreteUserFieldNamesWithCategoryNames$: Observable<{ id: string, name: string, categoryNames: { id: string, name: string }[] }[]>;

  constructor(
    private blueprints: BlueprintStore,
    private bookingTypes: BookingTypesStore,
    private bookingItems: BookingItemsStore,
    private eventTypes: EventTypesStore,
    private relations: RelationsStore,
    private roles: RolesStore,
    private userFields: UserFieldsStore
  ) {
    this.bookingItemNames$ = this.getBookingItemNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.bookingTypeNames$ = this.getBookingTypeNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.bookingStateNames$ = this.getBookingStateNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.bookingMetadataNames$ = this.getBookingMetadataNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.roleNames$ = this.getRoleNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.relationNamesWithCategoryNames$ = this.getRelationNamesWithCategoryNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.eventTypeGroupNames$ = this.getEventTypeGroupNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.blueprintNamesWithCategoryNames$ = this.getBlueprintNamesWithCategoryNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.userFields$ = this.getUserFields$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.stringUserFieldNames$ = this.getStringUserFieldNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.dateUserFieldNames$ = this.getDateUserFieldNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
    this.discreteUserFieldNamesWithCategoryNames$ = this.getDiscreteUserFieldNamesWithCategoryNames$().pipe(
      shareReplay({ refCount: false, bufferSize: 1 })
    );
  }

  private getBookingItemNames$(): Observable<{ id: string, name: string }[]> {
    return this.bookingItems.all().pipe(
      map(bookingItems =>
        bookingItems
          .map(bookingItem => ({ id: bookingItem.doc._id, name: bookingItem.doc.name ?? 'Undefined' }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getBookingTypeNames$(): Observable<{ id: string, name: string }[]> {
    return this.bookingTypes.all().pipe(
      map(bookingTypes =>
        bookingTypes
          .map(bookingType => ({ id: bookingType.doc._id, name: bookingType.doc.name ?? $localize `Undefined` }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getBookingOptionNamesForBookingItem$(bookingItemId: string): Observable<{ id: string, name: string }[]> {
    return this.bookingItems.getBookableOptions(bookingItemId).pipe(
      map(bookingOptions =>
        bookingOptions
          .map(bookingOption => ({ id: bookingOption.id, name: bookingOption.title ?? $localize `Undefined` }))
      )
    );
  }

  bookingOptionNamesForBookingItem$(bookingItemId: string): Observable<{ id: string, name: string }[]> {
    if (!(bookingItemId in this.bookingOptionNames$)) {
      this.bookingOptionNames$[bookingItemId] = this.getBookingOptionNamesForBookingItem$(bookingItemId).pipe(
        shareReplay({ refCount: false, bufferSize: 1 })
      );
    }
    return this.bookingOptionNames$[bookingItemId];
  }

  private getBookingStateNames$(): Observable<{ id: string, name: string }[]> {
    return of([
      { id: 'Draft', name: $localize `Draft` },
      { id: 'Payment pending', name: $localize `Payment pending` },
      { id: 'Paid', name: $localize `Paid` },
    ]);
  }

  private getBookingMetadataNames$(): Observable<{ id: string, name: string }[]> {
    return of([
      { id: 'bookingConfirmed', name: 'bookingConfirmed' },
    ]);
  }

  private getRoleNames$(): Observable<{ id: string, name: string }[]> {
    return this.roles.allWithSystem().pipe(
      map(roles =>
        roles
          .map(role => ({ id: role.doc._id, name: role.doc.title }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getRelationNamesWithCategoryNames$(): Observable<{ id: string, name: string, categoryNames: { id: string, name: string }[] }[]> {
    return this.relations.all().pipe(
      map(relations =>
        relations
          .map(relation => ({
            id: relation.doc._id,
            name: relation.doc.name,
            categoryNames: categoriesToFlatCategoryNames(relation.doc.categories)
          }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }
/*
  private getEventTypeNames$(): Observable<{ id: string, name: string }[]> {
    return this.eventTypes.search().pipe(
      map(eventTypes =>
        eventTypes.data
          .map(eventType => ({ id: eventType.doc._id, name: eventType.doc.name }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }
*/
  private getEventTypeGroupNames$(): Observable<{ id: string, name: string }[]> {
    // TODO: Returning GROUP IDs, filtering event types without next version
    // TODO: This has to be amended using API V2
    return this.eventTypes.search().pipe(
      map(eventTypes =>
        eventTypes.data
          .filter(eventType => eventType.doc.nextVersion === undefined)
          .map(eventType => ({ id: eventType.doc.versionGroupId, name: eventType.doc.name }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getBlueprintNamesWithCategoryNames$(): Observable<{ id: string, name: string, categoryNames: { id: string, name: string }[] }[]> {
    return this.blueprints.all().pipe(
      map(blueprints =>
        blueprints
          .filter(blueprint => blueprint.doc.blueprintType === 'discrete')
          .map(blueprint => ({
            id: blueprint.doc._id,
            name: blueprint.doc.name,
            categoryNames: categoriesToFlatCategoryNames(blueprint.doc.categories)
          }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getUserFields$(): Observable<any[]> {
    return this.userFields.all();
  }

  private getStringUserFieldNames$(): Observable<{ id: string, name: string }[]> {
    return this.userFields$.pipe(
      map(userFields =>
        userFields
          .filter(userField =>
            userField.doc.fieldType === 'string'
          )
          .map(userField => ({
            id: userField.doc._id,
            name: userField.doc.name
          }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getDateUserFieldNames$(): Observable<{ id: string, name: string }[]> {
    return this.userFields$.pipe(
      map(userFields =>
        userFields
          .filter(userField =>
            userField.doc.fieldType === 'date'
          )
          .map(userField => ({
            id: userField.doc._id,
            name: userField.doc.name
          }))
          .sort((a, b) => iCaseCompare(a.name, b.name))
      )
    );
  }

  private getDiscreteUserFieldNamesWithCategoryNames$():
    Observable<{ id: string, name: string, categoryNames: { id: string, name: string }[] }[]> {
      return this.userFields$.pipe(
        map(userFields =>
          userFields
            .filter(userField =>
              userField.doc.fieldType === 'discrete' || userField.doc.fieldType === 'discrete_multiple'
            )
            .map(userField => ({
              id: userField.doc._id,
              name: userField.doc.name,
              categoryNames: categoriesToFlatCategoryNames(userField.doc.categories)
            }))
            .sort((a, b) => iCaseCompare(a.name, b.name))
        )
      );
    }
}
